import { Type, type ObjectOptions, type Static, type TProperties } from '@sinclair/typebox';
import { createSchema } from '../../util/schema';

/**
 * HedgeFacts Analytics contracts.
 *
 * Request and response bodies are owned and fully validated by the Python domain (pydantic).
 * The BFF documents the fields callers rely on and forwards every other property unchanged
 * (`additionalProperties` is an open schema, so `safeParse` keeps unknown keys). Value limits (lengths,
 * dates) are left to pydantic, so a rejected value carries the HFAnalytics `REQUEST_VALIDATION_FAILED` details.
 */
const passThrough = <T extends TProperties>(properties: T, options: ObjectOptions) =>
  Type.Object(properties, { additionalProperties: Type.Unknown(), ...options });

const Row = Type.Record(Type.String(), Type.Unknown(), { description: 'Source row / record; keys are defined by the Python domain.' });
const Rows = Type.Array(Row);
const Warnings = Type.Optional(Type.Array(Type.String()));
const AnalyticsDate = Type.String({ description: 'ISO calendar date YYYY-MM-DD.' });

// ---------------------------------------------------------------------------------------
// Adapters
// ---------------------------------------------------------------------------------------

/** Galaxy/IPERS position + attribution rows → canonical positions and cashflow ledger. */
export const AnalyticsGalaxyIpersParseRequest = passThrough(
  {
    portfolio_id: Type.Optional(Type.String({ description: 'Defaults to BXIIU53I in the Python domain.' })),
    position_rows: Type.Array(Row, { description: 'Raw Galaxy/IPERS position rows (at least one).' }),
    attribution_rows: Type.Optional(Rows),
    source_name: Type.Optional(Type.String()),
    source_files: Type.Optional(Type.Record(Type.String(), Row)),
  },
  { $id: 'AnalyticsGalaxyIpersParseRequest' },
);
export type AnalyticsGalaxyIpersParseRequest = Static<typeof AnalyticsGalaxyIpersParseRequest>;
export const analyticsGalaxyIpersParseRequestSchema = createSchema(AnalyticsGalaxyIpersParseRequest);

export const AnalyticsGalaxyIpersParseResult = passThrough(
  {
    positions: Type.Optional(Rows),
    cashflows: Type.Optional(Rows),
    warnings: Warnings,
  },
  { $id: 'AnalyticsGalaxyIpersParseResult', description: 'Canonical positions, cashflow ledger, validation counts, source hashes and lineage.' },
);
export type AnalyticsGalaxyIpersParseResult = Static<typeof AnalyticsGalaxyIpersParseResult>;
export const analyticsGalaxyIpersParseResultSchema = createSchema(AnalyticsGalaxyIpersParseResult);

export const AnalyticsBrokerList = passThrough(
  {
    brokers: Type.Optional(Type.Array(Type.Object({ id: Type.String(), name: Type.String() }))),
    canonical_schema: Type.Optional(Type.String()),
    flat_position_schema: Type.Optional(Type.String()),
    canonical_position_fields: Type.Optional(Type.Array(Type.String())),
    live_instrument_types: Type.Optional(Type.Array(Type.String())),
  },
  { $id: 'AnalyticsBrokerList' },
);
export type AnalyticsBrokerList = Static<typeof AnalyticsBrokerList>;
export const analyticsBrokerListSchema = createSchema(AnalyticsBrokerList);

/** One broker CSV (`content`) or XLS/XLSX (`content_base64`) statement. */
export const AnalyticsBrokerFileParseRequest = passThrough(
  {
    broker: Type.String({ description: 'Broker adapter id from GET /analytics/adapters/brokers.' }),
    filename: Type.String(),
    content: Type.Optional(Type.String({ description: 'CSV text.' })),
    content_base64: Type.Optional(Type.String({ description: 'Base64 XLS/XLSX bytes.' })),
    portfolio_id: Type.Optional(Type.String()),
  },
  { $id: 'AnalyticsBrokerFileParseRequest' },
);
export type AnalyticsBrokerFileParseRequest = Static<typeof AnalyticsBrokerFileParseRequest>;
export const analyticsBrokerFileParseRequestSchema = createSchema(AnalyticsBrokerFileParseRequest);

export const AnalyticsBrokerFileParseResult = passThrough(
  {
    positions: Type.Optional(Rows),
    warnings: Warnings,
  },
  { $id: 'AnalyticsBrokerFileParseResult', description: 'Canonical positions plus incomplete positions retained for review.' },
);
export type AnalyticsBrokerFileParseResult = Static<typeof AnalyticsBrokerFileParseResult>;
export const analyticsBrokerFileParseResultSchema = createSchema(AnalyticsBrokerFileParseResult);

// ---------------------------------------------------------------------------------------
// Curves / market data
// ---------------------------------------------------------------------------------------

/** Controlled input quotes → USD SOFR OIS curve. */
export const AnalyticsCurveBuildRequest = passThrough(
  {
    analysis_date: AnalyticsDate,
    curve_name: Type.Optional(Type.String({ description: 'Defaults to USD-SOFR-OIS.' })),
    quotes: Type.Array(Row, { description: 'Par OIS quotes / zero nodes with tenor, rate, source, quality (at least two).' }),
    sample_tenors_years: Type.Optional(Type.Array(Type.Number())),
  },
  { $id: 'AnalyticsCurveBuildRequest' },
);
export type AnalyticsCurveBuildRequest = Static<typeof AnalyticsCurveBuildRequest>;
export const analyticsCurveBuildRequestSchema = createSchema(AnalyticsCurveBuildRequest);

export const AnalyticsCurveBuild = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    curve_id: Type.Optional(Type.String()),
    curve_name: Type.Optional(Type.String()),
    analysis_date: Type.Optional(Type.String()),
    built_at_utc: Type.Optional(Type.String()),
    engine: Type.Optional(Type.String()),
    status: Type.Optional(Type.String()),
    input_controls: Type.Optional(Row),
    quotes: Type.Optional(Rows),
    zero_curve: Type.Optional(Rows),
    warnings: Warnings,
  },
  { $id: 'AnalyticsCurveBuild' },
);
export type AnalyticsCurveBuild = Static<typeof AnalyticsCurveBuild>;
export const analyticsCurveBuildSchema = createSchema(AnalyticsCurveBuild);

export const AnalyticsCurveValidation = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    curve_id: Type.Optional(Type.String()),
    status: Type.Optional(Type.String()),
    input_controls: Type.Optional(Row),
    warnings: Warnings,
  },
  { $id: 'AnalyticsCurveValidation' },
);
export type AnalyticsCurveValidation = Static<typeof AnalyticsCurveValidation>;
export const analyticsCurveValidationSchema = createSchema(AnalyticsCurveValidation);

export const AnalyticsFredHistoryRequest = passThrough(
  {
    start_date: AnalyticsDate,
    end_date: AnalyticsDate,
  },
  { $id: 'AnalyticsFredHistoryRequest' },
);
export type AnalyticsFredHistoryRequest = Static<typeof AnalyticsFredHistoryRequest>;
export const analyticsFredHistoryRequestSchema = createSchema(AnalyticsFredHistoryRequest);

export const AnalyticsFredHistory = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
  },
  { $id: 'AnalyticsFredHistory', description: 'Official FRED SOFR/Treasury panel used for historical shocks.' },
);
export type AnalyticsFredHistory = Static<typeof AnalyticsFredHistory>;
export const analyticsFredHistorySchema = createSchema(AnalyticsFredHistory);

/** Optional as-of date for latest-observation and demo reads. */
export const AnalyticsAsOfQuery = Type.Object(
  {
    as_of: Type.Optional(AnalyticsDate),
  },
  { $id: 'AnalyticsAsOfQuery' },
);
export type AnalyticsAsOfQuery = Static<typeof AnalyticsAsOfQuery>;
export const analyticsAsOfQuerySchema = createSchema(AnalyticsAsOfQuery);

export const AnalyticsOfficialSofrLatest = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    source: Type.Optional(Type.String()),
    source_page: Type.Optional(Type.String()),
    retrieved_at_utc: Type.Optional(Type.String()),
    response_sha256: Type.Optional(Type.String()),
    observation: Type.Optional(Row),
  },
  { $id: 'AnalyticsOfficialSofrLatest' },
);
export type AnalyticsOfficialSofrLatest = Static<typeof AnalyticsOfficialSofrLatest>;
export const analyticsOfficialSofrLatestSchema = createSchema(AnalyticsOfficialSofrLatest);

/** Official NY Fed SOFR + public SDR transactions → curve. */
export const AnalyticsOfficialTransactionsCurveRequest = passThrough(
  {
    analysis_date: AnalyticsDate,
    transactions: Type.Array(Row, { description: 'Public swap data repository records (at least one).' }),
    sdr_source: Type.Optional(Type.String()),
    sdr_source_reference: Type.Optional(Type.String()),
    min_trades_per_tenor: Type.Optional(Type.Integer()),
    near_spot_max_calendar_days: Type.Optional(Type.Integer()),
    broker: Type.Optional(Type.String()),
    statement_valuation_timestamp: Type.Optional(Type.String()),
    statement_timezone: Type.Optional(Type.String()),
    close_lookback_hours: Type.Optional(Type.Integer()),
    close_start_hour_utc: Type.Optional(Type.Integer()),
    close_end_hour_utc: Type.Optional(Type.Integer()),
    min_close_trades: Type.Optional(Type.Integer()),
    close_liquidity_threshold: Type.Optional(Type.Integer()),
    sample_tenors_years: Type.Optional(Type.Array(Type.Number())),
    unofficial_benchmark_quotes: Type.Optional(Rows),
    unofficial_benchmark_rate_unit: Type.Optional(Type.String()),
  },
  { $id: 'AnalyticsOfficialTransactionsCurveRequest' },
);
export type AnalyticsOfficialTransactionsCurveRequest = Static<typeof AnalyticsOfficialTransactionsCurveRequest>;
export const analyticsOfficialTransactionsCurveRequestSchema = createSchema(AnalyticsOfficialTransactionsCurveRequest);

/** Exact-date public DTCC records aligned to a broker statement valuation time. */
export const AnalyticsBrokerContextCurveRequest = passThrough(
  {
    analysis_date: AnalyticsDate,
    broker: Type.String(),
    statement_valuation_timestamp: Type.Optional(Type.String()),
    statement_timezone: Type.Optional(Type.String()),
    close_lookback_hours: Type.Optional(Type.Integer()),
    min_trades_per_tenor: Type.Optional(Type.Integer()),
    min_close_trades: Type.Optional(Type.Integer()),
    close_liquidity_threshold: Type.Optional(Type.Integer()),
    near_spot_max_calendar_days: Type.Optional(Type.Integer()),
    sample_tenors_years: Type.Optional(Type.Array(Type.Number())),
  },
  { $id: 'AnalyticsBrokerContextCurveRequest' },
);
export type AnalyticsBrokerContextCurveRequest = Static<typeof AnalyticsBrokerContextCurveRequest>;
export const analyticsBrokerContextCurveRequestSchema = createSchema(AnalyticsBrokerContextCurveRequest);

/** Dated public (CheckMySwap) snapshot → independent non-production curve. */
export const AnalyticsPublicSnapshotCurveRequest = passThrough(
  {
    analysis_date: AnalyticsDate,
    provider: Type.Optional(Type.String({ description: 'Defaults to CheckMySwap.' })),
    snapshot: Row,
    sample_tenors_years: Type.Optional(Type.Array(Type.Number())),
  },
  { $id: 'AnalyticsPublicSnapshotCurveRequest' },
);
export type AnalyticsPublicSnapshotCurveRequest = Static<typeof AnalyticsPublicSnapshotCurveRequest>;
export const analyticsPublicSnapshotCurveRequestSchema = createSchema(AnalyticsPublicSnapshotCurveRequest);

/** Broker valuation observations reconciled without treating them as OIS nodes. */
export const AnalyticsSourceAnalyticsCurveRequest = passThrough(
  {
    analysis_date: AnalyticsDate,
    positions: Type.Array(Row, { description: 'At least one position.' }),
    overnight_sofr: Type.Optional(Type.Number()),
    sample_tenors_years: Type.Optional(Type.Array(Type.Number())),
  },
  { $id: 'AnalyticsSourceAnalyticsCurveRequest' },
);
export type AnalyticsSourceAnalyticsCurveRequest = Static<typeof AnalyticsSourceAnalyticsCurveRequest>;
export const analyticsSourceAnalyticsCurveRequestSchema = createSchema(AnalyticsSourceAnalyticsCurveRequest);

/** Deprecated caller-supplied transactions + overnight rate → curve. */
export const AnalyticsTransactionsCurveRequest = passThrough(
  {
    analysis_date: AnalyticsDate,
    overnight_rate: Type.Number(),
    transactions: Type.Optional(Rows),
    overnight_source: Type.Optional(Type.String()),
    rate_unit: Type.Optional(Type.String()),
    min_trades_per_tenor: Type.Optional(Type.Integer()),
    source_reference: Type.Optional(Type.String()),
    benchmark_quotes: Type.Optional(Rows),
    benchmark_rate_unit: Type.Optional(Type.String()),
  },
  { $id: 'AnalyticsTransactionsCurveRequest' },
);
export type AnalyticsTransactionsCurveRequest = Static<typeof AnalyticsTransactionsCurveRequest>;
export const analyticsTransactionsCurveRequestSchema = createSchema(AnalyticsTransactionsCurveRequest);

/** Zero curve result shared by the from-* curve routes and POST /analytics/curves/sofr. */
export const AnalyticsCurve = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    analysis_date: Type.Optional(Type.String()),
    valuation_curve_source: Type.Optional(Type.String()),
    status: Type.Optional(Type.String()),
    curve_quotes: Type.Optional(Rows),
    zero_curve: Type.Optional(Rows),
    warnings: Warnings,
  },
  { $id: 'AnalyticsCurve' },
);
export type AnalyticsCurve = Static<typeof AnalyticsCurve>;
export const analyticsCurveSchema = createSchema(AnalyticsCurve);

// ---------------------------------------------------------------------------------------
// Rates portfolio analytics (analyze_portfolio)
// ---------------------------------------------------------------------------------------

/** PortfolioAnalysisRequest: positions + market data for valuation, sensitivities, ladders and historical risk. */
export const AnalyticsPortfolioRequest = passThrough(
  {
    analysis_date: AnalyticsDate,
    portfolio_id: Type.Optional(Type.String()),
    positions: Type.Optional(Rows),
    ois_quotes: Type.Optional(Rows),
    forecast_ois_quotes: Type.Optional(Rows),
    valuation_conventions: Type.Optional(Row),
    fred_history: Type.Optional(Rows),
    confidence_levels: Type.Optional(Type.Array(Type.Number())),
    confidence: Type.Optional(Type.Number()),
    horizon_days: Type.Optional(Type.Integer()),
    ladder_rate_shocks_bp: Type.Optional(Type.Array(Type.Number())),
    ladder_normal_vol_shocks_bp: Type.Optional(Type.Array(Type.Number())),
  },
  { $id: 'AnalyticsPortfolioRequest' },
);
export type AnalyticsPortfolioRequest = Static<typeof AnalyticsPortfolioRequest>;
export const analyticsPortfolioRequestSchema = createSchema(AnalyticsPortfolioRequest);

export const AnalyticsValuation = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    analysis_date: Type.Optional(Type.String()),
    valuation_curve_source: Type.Optional(Type.String()),
    valuation_conventions: Type.Optional(Row),
    curve_roles: Type.Optional(Row),
    valuations: Type.Optional(Rows),
    warnings: Warnings,
  },
  { $id: 'AnalyticsValuation' },
);
export type AnalyticsValuation = Static<typeof AnalyticsValuation>;
export const analyticsValuationSchema = createSchema(AnalyticsValuation);

export const AnalyticsPortfolioSummary = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    analysis_date: Type.Optional(Type.String()),
    portfolio_positions: Type.Optional(Type.Unknown()),
    valuation_curve_source: Type.Optional(Type.String()),
    valuation_conventions: Type.Optional(Row),
    curve_roles: Type.Optional(Row),
    exposure: Type.Optional(Type.Unknown()),
    warnings: Warnings,
  },
  { $id: 'AnalyticsPortfolioSummary' },
);
export type AnalyticsPortfolioSummary = Static<typeof AnalyticsPortfolioSummary>;
export const analyticsPortfolioSummarySchema = createSchema(AnalyticsPortfolioSummary);

export const AnalyticsPortfolioPositions = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    analysis_date: Type.Optional(Type.String()),
    positions: Type.Optional(Rows),
    warnings: Warnings,
  },
  { $id: 'AnalyticsPortfolioPositions' },
);
export type AnalyticsPortfolioPositions = Static<typeof AnalyticsPortfolioPositions>;
export const analyticsPortfolioPositionsSchema = createSchema(AnalyticsPortfolioPositions);

export const AnalyticsHistoricalRisk = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    analysis_date: Type.Optional(Type.String()),
    valuation_curve_source: Type.Optional(Type.String()),
    historical_risk: Type.Optional(Row),
    warnings: Warnings,
  },
  { $id: 'AnalyticsHistoricalRisk' },
);
export type AnalyticsHistoricalRisk = Static<typeof AnalyticsHistoricalRisk>;
export const analyticsHistoricalRiskSchema = createSchema(AnalyticsHistoricalRisk);

export const AnalyticsLadders = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    analysis_date: Type.Optional(Type.String()),
    valuation_curve_source: Type.Optional(Type.String()),
    ladders: Type.Optional(Type.Unknown()),
    warnings: Warnings,
  },
  { $id: 'AnalyticsLadders' },
);
export type AnalyticsLadders = Static<typeof AnalyticsLadders>;
export const analyticsLaddersSchema = createSchema(AnalyticsLadders);

export const AnalyticsSensitivities = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    analysis_date: Type.Optional(Type.String()),
    valuation_curve_source: Type.Optional(Type.String()),
    sensitivities: Type.Optional(Type.Unknown()),
    warnings: Warnings,
  },
  { $id: 'AnalyticsSensitivities' },
);
export type AnalyticsSensitivities = Static<typeof AnalyticsSensitivities>;
export const analyticsSensitivitiesSchema = createSchema(AnalyticsSensitivities);

/** BFF aggregate: price + sensitivities + ladders invoked in parallel for the same portfolio request. */
export const AnalyticsRiskPack = Type.Object(
  {
    valuation: AnalyticsValuation,
    sensitivities: AnalyticsSensitivities,
    ladders: AnalyticsLadders,
  },
  { $id: 'AnalyticsRiskPack' },
);
export type AnalyticsRiskPack = Static<typeof AnalyticsRiskPack>;
export const analyticsRiskPackSchema = createSchema(AnalyticsRiskPack);

export const AnalyticsDemoSummary = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    analysis_date: Type.Optional(Type.String()),
    warnings: Warnings,
  },
  { $id: 'AnalyticsDemoSummary', description: 'Recalculated frozen Galaxy/IPERS demonstration snapshot.' },
);
export type AnalyticsDemoSummary = Static<typeof AnalyticsDemoSummary>;
export const analyticsDemoSummarySchema = createSchema(AnalyticsDemoSummary);

// ---------------------------------------------------------------------------------------
// VaR engine
// ---------------------------------------------------------------------------------------

export const AnalyticsVarDiagnostics = passThrough(
  {
    status: Type.Optional(Type.String()),
    service: Type.Optional(Type.String()),
    schema_version: Type.Optional(Type.String()),
    engine: Type.Optional(Type.String()),
    engine_version: Type.Optional(Type.String()),
    generated_at_utc: Type.Optional(Type.String()),
    checks: Type.Optional(Rows),
    self_test: Type.Optional(Row),
  },
  { $id: 'AnalyticsVarDiagnostics' },
);
export type AnalyticsVarDiagnostics = Static<typeof AnalyticsVarDiagnostics>;
export const analyticsVarDiagnosticsSchema = createSchema(AnalyticsVarDiagnostics);

/** Asset-agnostic scenario-by-position P&L matrix. */
export const AnalyticsHistoricalVarRequest = passThrough(
  {
    portfolio_id: Type.String(),
    valuation_date: AnalyticsDate,
    horizon_days: Type.Optional(Type.Integer()),
    currency: Type.Optional(Type.String()),
    confidence_levels: Type.Optional(Type.Array(Type.Number())),
    scenarios: Type.Array(Row, { description: 'Rows of scenario_id, pnl_by_position, probability, as_of (minimum 20).' }),
  },
  { $id: 'AnalyticsHistoricalVarRequest' },
);
export type AnalyticsHistoricalVarRequest = Static<typeof AnalyticsHistoricalVarRequest>;
export const analyticsHistoricalVarRequestSchema = createSchema(AnalyticsHistoricalVarRequest);

export const AnalyticsHistoricalVar = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    portfolio_id: Type.Optional(Type.String()),
    valuation_date: Type.Optional(Type.String()),
    horizon_days: Type.Optional(Type.Integer()),
    currency: Type.Optional(Type.String()),
    scenario_count: Type.Optional(Type.Integer()),
    position_count: Type.Optional(Type.Integer()),
    diagnostics: Type.Optional(Row),
  },
  { $id: 'AnalyticsHistoricalVar', description: 'Weighted empirical VaR / expected shortfall, position allocations and tail diagnostics.' },
);
export type AnalyticsHistoricalVar = Static<typeof AnalyticsHistoricalVar>;
export const analyticsHistoricalVarSchema = createSchema(AnalyticsHistoricalVar);

export const AnalyticsMultiAssetSchema = passThrough({}, { $id: 'AnalyticsMultiAssetSchema', description: 'JSON Schema of MultiAssetVarRequest.' });
export type AnalyticsMultiAssetSchema = Static<typeof AnalyticsMultiAssetSchema>;
export const analyticsMultiAssetSchemaSchema = createSchema(AnalyticsMultiAssetSchema);

export const AnalyticsCoverageValidationRequest = passThrough(
  {
    realized_losses: Type.Array(Type.Number(), { description: 'At least one realised loss.' }),
    var_forecasts: Type.Record(Type.String(), Type.Array(Type.Number()), {
      description: 'Confidence level (as string key) → aligned VaR forecasts.',
    }),
    significance: Type.Optional(Type.Number()),
  },
  { $id: 'AnalyticsCoverageValidationRequest' },
);
export type AnalyticsCoverageValidationRequest = Static<typeof AnalyticsCoverageValidationRequest>;
export const analyticsCoverageValidationRequestSchema = createSchema(AnalyticsCoverageValidationRequest);

export const AnalyticsCoverageValidation = passThrough(
  {
    status: Type.Optional(Type.String()),
    release_effect: Type.Optional(Type.String()),
    checks: Type.Optional(Rows),
  },
  { $id: 'AnalyticsCoverageValidation' },
);
export type AnalyticsCoverageValidation = Static<typeof AnalyticsCoverageValidation>;
export const analyticsCoverageValidationSchema = createSchema(AnalyticsCoverageValidation);

export const AnalyticsStationarityValidationRequest = passThrough(
  {
    factor_history: Type.Record(Type.String(), Type.Array(Type.Number()), { description: 'Factor id → aligned finite factor changes.' }),
    significance: Type.Optional(Type.Number()),
    var_lags: Type.Optional(Type.Integer()),
  },
  { $id: 'AnalyticsStationarityValidationRequest' },
);
export type AnalyticsStationarityValidationRequest = Static<typeof AnalyticsStationarityValidationRequest>;
export const analyticsStationarityValidationRequestSchema = createSchema(AnalyticsStationarityValidationRequest);

export const AnalyticsStationarityValidation = passThrough(
  {
    status: Type.Optional(Type.String()),
  },
  { $id: 'AnalyticsStationarityValidation', description: 'ADF/KPSS marginal checks and joint VAR stability diagnostic.' },
);
export type AnalyticsStationarityValidation = Static<typeof AnalyticsStationarityValidation>;
export const analyticsStationarityValidationSchema = createSchema(AnalyticsStationarityValidation);

/** hf.multi_asset canonical cross-asset portfolio (instruments, positions, risk factors, market data). */
export const AnalyticsCanonicalPortfolio = passThrough(
  {
    portfolio_id: Type.String(),
    name: Type.Optional(Type.String()),
    valuation_context: Row,
    sources: Type.Optional(Rows),
    instruments: Type.Array(Row, { description: 'At least one instrument.' }),
    positions: Type.Array(Row, { description: 'At least one position.' }),
    risk_factors: Type.Optional(Rows),
    market_data: Type.Optional(Rows),
    extensions: Type.Optional(Row),
  },
  { $id: 'AnalyticsCanonicalPortfolio' },
);
export type AnalyticsCanonicalPortfolio = Static<typeof AnalyticsCanonicalPortfolio>;
export const analyticsCanonicalPortfolioSchema = createSchema(AnalyticsCanonicalPortfolio);

export const AnalyticsCanonicalPortfolioValuation = passThrough(
  {
    portfolio_id: Type.Optional(Type.String()),
    valuation_date: Type.Optional(Type.String()),
    base_currency: Type.Optional(Type.String()),
    portfolio_value: Type.Optional(Type.Number()),
    positions: Type.Optional(Rows),
  },
  { $id: 'AnalyticsCanonicalPortfolioValuation' },
);
export type AnalyticsCanonicalPortfolioValuation = Static<typeof AnalyticsCanonicalPortfolioValuation>;
export const analyticsCanonicalPortfolioValuationSchema = createSchema(AnalyticsCanonicalPortfolioValuation);

export const AnalyticsCanonicalPortfolioGreeks = passThrough(
  {
    portfolio_id: Type.Optional(Type.String()),
    valuation_date: Type.Optional(Type.String()),
    positions: Type.Optional(Type.Record(Type.String(), Row, { description: 'position_id → base_value, delta, gamma, bump_sizes.' })),
  },
  { $id: 'AnalyticsCanonicalPortfolioGreeks' },
);
export type AnalyticsCanonicalPortfolioGreeks = Static<typeof AnalyticsCanonicalPortfolioGreeks>;
export const analyticsCanonicalPortfolioGreeksSchema = createSchema(AnalyticsCanonicalPortfolioGreeks);

/** Canonical portfolio + risk configuration + scenarios (see GET /analytics/var/multi-asset/schema). */
export const AnalyticsMultiAssetVarRequest = passThrough(
  {
    schema_id: Type.Optional(Type.String()),
    schema_version: Type.Optional(Type.String()),
    portfolio: AnalyticsCanonicalPortfolio,
    risk: Type.Optional(Row),
    scenarios: Type.Array(Row, { description: 'Scenario valuations (minimum 20).' }),
    extensions: Type.Optional(Row),
  },
  { $id: 'AnalyticsMultiAssetVarRequest' },
);
export type AnalyticsMultiAssetVarRequest = Static<typeof AnalyticsMultiAssetVarRequest>;
export const analyticsMultiAssetVarRequestSchema = createSchema(AnalyticsMultiAssetVarRequest);

export const AnalyticsMultiAssetVar = passThrough(
  {
    schema_version: Type.Optional(Type.String()),
    portfolio_id: Type.Optional(Type.String()),
  },
  { $id: 'AnalyticsMultiAssetVar', description: 'Reconciled VaR/ES aggregation for one canonical cross-asset portfolio.' },
);
export type AnalyticsMultiAssetVar = Static<typeof AnalyticsMultiAssetVar>;
export const analyticsMultiAssetVarSchema = createSchema(AnalyticsMultiAssetVar);

// ---------------------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------------------

/** HFAnalytics error body. Analytics routes answer failures with this (`application/json`), not Problem Details. */
export const AnalyticsErrorResponse = Type.Object(
  {
    error: Type.Object({
      code: Type.String({
        description:
          'REQUEST_VALIDATION_FAILED, INVALID_ANALYTICS_INPUT, VAR_INPUT_VALIDATION_FAILED, HTTP_ERROR, INTERNAL_CALCULATION_ERROR, ANALYTICS_UNAVAILABLE, INVALID_ANALYTICS_RESPONSE, or a VaR / pricing code.',
      }),
      message: Type.String(),
      request_id: Type.Optional(Type.String({ description: 'Locates the server trace.' })),
      details: Type.Optional(
        Type.Array(Row, { description: 'REQUEST_VALIDATION_FAILED only: one row per rejected field (type, loc, msg, input, ctx).' }),
      ),
      diagnostics: Type.Optional(Row),
    }),
  },
  { $id: 'AnalyticsErrorResponse' },
);
export type AnalyticsErrorResponse = Static<typeof AnalyticsErrorResponse>;
export const analyticsErrorResponseSchema = createSchema(AnalyticsErrorResponse);

// ---------------------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------------------

export const AnalyticsHealth = Type.Object(
  {
    ok: Type.Boolean(),
    detail: Type.Optional(Type.String()),
  },
  { $id: 'AnalyticsHealth' },
);
export type AnalyticsHealth = Static<typeof AnalyticsHealth>;
export const analyticsHealthSchema = createSchema(AnalyticsHealth);
